import { graphql, readInlineData } from 'react-relay';
import * as tracking from 'dibs-tracking';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { emailLoginLinkSendMutation } from 'dibs-buyer-layout/exports/emailLoginLinkSendMutation';
import { GOOGLE, FACEBOOK, APPLE } from 'dibs-buyer-layout/exports/socialConstants';
import { getBuyerId, isMasquerading } from 'dibs-cookie-jar';
import {
    checkFacebookStatus,
    checkGoogleStatus,
    getUserEmail,
    isSocialNetworkConnected,
    logoutUser,
} from '../helpers/checkoutUser';
import { trackProceedToCheckout, trackEmbeddedRegistration } from '../helpers/checkoutTracking';
import { clearAutoOfferModalData } from '../helpers/checkoutStorage';
import { getEmailLoginLinkUrlRedirect } from '../helpers/emailLoginLinkHelpers';
import { deferActionIfInBackground } from '../prerender/deferActionIfInBackground';
import { transactionBuyerChangedAction } from './commonActions';

const AUTH = authModalLoader.constants;

function authUser({ email, action, flow, additionalProps, dispatch, gaLabel }) {
    let ga = {
        category: AUTH.LOGIN,
        label: gaLabel || 'checkout',
    };

    if (flow === AUTH.STALE_LOGIN_CHECKOUT_FLOW) {
        ga = { ...ga, action: 're-enter password' };
    } else if (flow === AUTH.STALE_LOGIN_EMAIL_ONLY_CHECKOUT_FLOW) {
        ga = { ...ga, action: 'verify email link' };
    }

    deferActionIfInBackground(() => {
        authModalLoader
            .show({ email, action, flow, additionalProps, ga })
            .then(({ modal, err }) => {
                if (err) {
                    switch (flow) {
                        case AUTH.STALE_LOGIN_CHECKOUT_FLOW:
                        case AUTH.STALE_LOGIN_EMAIL_ONLY_CHECKOUT_FLOW:
                            logoutUser();
                            dispatch({ type: 'TRANSACTION_BUYER_LOGOUT' });
                            break;
                        case AUTH.EMBEDDED_CHECKOUT_FLOW:
                            trackEmbeddedRegistration();
                    }
                    trackProceedToCheckout({ modal, flow });
                } else {
                    const buyerId = getBuyerId(document.cookie);
                    switch (flow) {
                        case AUTH.LOGIN:
                        case AUTH.EMBEDDED_CHECKOUT_FLOW:
                        case AUTH.GUEST_CHECKOUT_ACCOUNT_EXISTS_FLOW:
                            if (modal === AUTH.LOGIN) {
                                dispatch(
                                    transactionBuyerChangedAction({
                                        buyerId,
                                    })
                                );
                            }
                            break;
                        case AUTH.STALE_LOGIN_CHECKOUT_FLOW:
                        case AUTH.STALE_LOGIN_EMAIL_ONLY_CHECKOUT_FLOW:
                            dispatch(
                                transactionBuyerChangedAction({
                                    buyerId,
                                })
                            );
                    }
                    clearAutoOfferModalData();
                }
            });
    });
}

function getAuthModalAdditionalProps({ userExistsMetadata: userExistsMetadataRef }) {
    const userExistsMetadata = readInlineData(
        graphql`
            fragment checkoutAuthActions_userExistsMetadata on UserExistsMetadataType @inline {
                hasPassword
                hasFacebook
                hasGoogle
                hasApple
                emailOnly
            }
        `,
        userExistsMetadataRef
    );
    const { hasPassword, hasFacebook, hasGoogle, hasApple, emailOnly } = userExistsMetadata || {};
    const hideRegularLogin = !hasPassword && !emailOnly;
    const hideSocialLogin = !hasFacebook && !hasGoogle && !hasApple;
    const hideSsoOptionsList = !hideSocialLogin
        ? Object.entries({
              [GOOGLE]: hasGoogle,
              [FACEBOOK]: hasFacebook,
              [APPLE]: hasApple,
          })
              .filter(([, hasSso]) => !hasSso)
              .map(([ssoName]) => ssoName)
        : undefined;

    return {
        hasPassword,
        loginStep: hasPassword ? 2 : undefined,
        hideRegularLogin,
        hideSocialLogin,
        hideSsoOptionsList,
    };
}

/**
 * @param {{email: string | null | undefined, dispatch: $TSFixMe, gaLabel?: string, userExistsMetadata?: $TSFixMe}} options
 */
export function openLoginModal({ email, dispatch, gaLabel, userExistsMetadata }) {
    const { loginStep, hideRegularLogin, hideSocialLogin, hideSsoOptionsList } =
        getAuthModalAdditionalProps({ userExistsMetadata });

    authUser({
        action: AUTH.LOGIN,
        flow: AUTH.EMBEDDED_CHECKOUT_FLOW,
        email: email || getUserEmail(),
        gaLabel,
        dispatch,
        additionalProps: {
            hideMarketingOptIn: true,
            loginStep,
            hideRegularLogin,
            hideSocialLogin,
            hideSsoOptionsList,
        },
    });
}

export function openGuestCheckoutEmailExistsLoginModal({ email, userExistsMetadata, dispatch }) {
    const { loginStep, hideRegularLogin, hideSocialLogin, hideSsoOptionsList } =
        getAuthModalAdditionalProps({ userExistsMetadata });
    authUser({
        action: AUTH.LOGIN,
        flow: AUTH.GUEST_CHECKOUT_ACCOUNT_EXISTS_FLOW,
        email,
        gaLabel: 'checkout - login to complete purchase',
        dispatch,
        additionalProps: {
            hideMarketingOptIn: true,
            loginStep,
            hideRegularLogin,
            hideSocialLogin,
            hideSsoOptionsList,
        },
    });
}

export async function openGuestCheckoutEmailLinkSentModal({ email, environment, dispatch }) {
    const urlRedirect = getEmailLoginLinkUrlRedirect();
    tracking.trackEvent({
        category: 'login',
        action: 'login link email submitted',
        label: 'checkout - verify your email',
        isInteractive: true,
        eventName: 'login',
        interaction_type: 'user login',
        step_interaction_name: 'login link email submited',
    });
    await emailLoginLinkSendMutation(environment, {
        email,
        urlRedirect,
    });
    authUser({
        action: AUTH.REQUEST_PASSWORD_EMAIL,
        flow: AUTH.GUEST_CHECKOUT_EMAIL_LINK_SENT_FLOW,
        email,
        gaLabel: 'checkout - verify your email',
        additionalProps: {
            urlRedirect,
        },
        dispatch,
    });
}

export function openAuthModal({ buyer: buyerRef, dispatch, environment }) {
    const buyer = readInlineData(
        graphql`
            fragment checkoutAuthActions_buyer on User @inline {
                profile {
                    email
                }
                authMetadata {
                    emailOnly
                }
                socialNetworks {
                    ... on FacebookSocialNetwork {
                        status
                        socialId
                        socialNetwork {
                            name
                        }
                    }
                    ... on GoogleSocialNetwork {
                        status
                        socialId
                        socialNetwork {
                            name
                        }
                    }
                    ... on AppleSocialNetwork {
                        status
                        socialId
                        socialNetwork {
                            name
                        }
                    }
                }
            }
        `,
        buyerRef
    );
    const { profile, socialNetworks } = buyer || {};
    const { email } = profile || {};
    const isFacebookConnected = isSocialNetworkConnected({
        socialNetworks,
        socialNetworkName: 'FACEBOOK',
    });
    const isGoogleConnected = isSocialNetworkConnected({
        socialNetworks,
        socialNetworkName: 'GOOGLE',
    });
    const isAppleConnected = isSocialNetworkConnected({
        socialNetworks,
        socialNetworkName: 'APPLE',
    });
    if (buyer?.authMetadata?.emailOnly) {
        tracking.trackEvent({
            category: 'login',
            action: 'login entry',
            label: 'checkout - verify your account modal',
            isInteractive: true,
            eventName: 'login',
            interaction_type: 'user login',
            step_interaction_name: 'entry',
        });
    }
    const openModal = () => {
        authUser({
            action: buyer?.authMetadata?.emailOnly ? AUTH.REQUEST_PASSWORD_EMAIL : AUTH.LOGIN,
            flow: buyer?.authMetadata?.emailOnly
                ? AUTH.STALE_LOGIN_EMAIL_ONLY_CHECKOUT_FLOW
                : AUTH.STALE_LOGIN_CHECKOUT_FLOW,
            // If buyer is masquerading, stale login modal should use internal user's email - not the one from buyer's profile.
            email: (!isMasquerading(document.cookie) && email) || getUserEmail(),
            additionalProps: {
                isFacebookConnected,
                isGoogleConnected,
                isAppleConnected,
                urlRedirect: getEmailLoginLinkUrlRedirect(),
            },
            dispatch,
        });
    };
    // add isAppleConnected check when 3rd party SSO adds method to check if user is connected to apple.
    if (isFacebookConnected) {
        checkFacebookStatus({ socialNetworks, openModal, relayEnvironment: environment });
    } else if (isGoogleConnected) {
        checkGoogleStatus({ socialNetworks, openModal, relayEnvironment: environment });
    } else {
        openModal();
    }
}
